// @ts-strict-ignore
import { DateTimeOffset, ReminderInputBlock } from '@coast/schemas';
import moment from 'moment';
import { useEffect } from 'react';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { Reminder } from '@shared/components/blocks/types';
import { API } from '@shared/types/api';
import { useMountedRef } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

const defaultDeltaOptions: DateTimeOffset[] = [
  { count: 0, unit: 'second' },
  { count: -5, unit: 'minute' },
  { count: -10, unit: 'minute' },
  { count: -30, unit: 'minute' },
  { count: -1, unit: 'hour' },
  { count: -2, unit: 'hour' },
  { count: -1, unit: 'day' },
  { count: -2, unit: 'day' },
  { count: 1, unit: 'day' },
  { count: 2, unit: 'day' },
  { count: 7, unit: 'day' },
];

function formatRelativeTo(
  dateFieldName: ReminderInputBlock['dateFieldName'],
): API.ReminderRelativeTo {
  if (dateFieldName === 'startDate') {
    return 'START';
  }
  if (dateFieldName === 'endDate') {
    return 'END';
  }
  return undefined;
}

export function format(duration: moment.Duration): string {
  if (duration.valueOf() === 0) {
    return i18n('card.blocks.reminder.atTimeOfEvent');
  }

  const parts: string[] = [];
  if (duration.days()) {
    parts.push(i18n('card.blocks.reminder.nDays', { count: Math.abs(duration.days()) }));
  }
  if (duration.hours()) {
    parts.push(i18n('card.blocks.reminder.nHours', { count: Math.abs(duration.hours()) }));
  }
  if (duration.minutes()) {
    parts.push(i18n('card.blocks.reminder.nMinutes', { count: Math.abs(duration.minutes()) }));
  }
  if (duration.seconds()) {
    parts.push(i18n('card.blocks.reminder.nSeconds', { count: Math.abs(duration.seconds()) }));
  }

  const durationString = parts.join(', ');
  if ((duration.valueOf() as number) > 0) {
    return i18n('card.blocks.reminder.durationAfter', { duration: durationString });
  } else {
    return i18n('card.blocks.reminder.durationBefore', { duration: durationString });
  }
}

export function dateTimeOffsetToReminder(
  delta: DateTimeOffset,
  block: ReminderInputBlock,
): Reminder {
  return {
    duration: moment.duration(delta.count, delta.unit).toISOString(),
    relativeTo: formatRelativeTo(block.dateFieldName),
    recipientFieldName: block.recipientFieldName,
    conditionalSelector: block.shouldSendSelector,
  };
}

function getDefaultReminder(block: ReminderInputBlock): Reminder {
  const unit = block.delta?.unit || 'day';
  const count = block.delta?.count || 0;
  return {
    relativeTo: formatRelativeTo(block.dateFieldName),
    duration: moment.duration(count, unit).toISOString(),
    recipientFieldName: block.recipientFieldName,
    conditionalSelector: block.shouldSendSelector,
  };
}

export function useReminderOptions(block: ReminderInputBlock): DateTimeOffset[] {
  const deltaOptions = block.deltaOptions || defaultDeltaOptions;
  return deltaOptions;
}

export function useSetDefaultReminder(block: ReminderInputBlock) {
  const { values, setFieldValue } = useCardFormContext();
  const reminders = values[block.name];
  const dateFieldValue = values[block.dateFieldName];
  const mounted = useMountedRef();

  useEffect(() => {
    // Note(fant): we don't want to set a default reminder if the
    // component was just mounted (eg edit form opened with no
    // reminder set). We only want to update the reminder if a date
    // field has just been set.
    const shouldSetDefault = !!dateFieldValue && !reminders.length && mounted.current;
    if (shouldSetDefault) {
      setFieldValue(block.name, [getDefaultReminder(block)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFieldValue, mounted]);
}

export function useClearReminderIfNoDateField(block: ReminderInputBlock) {
  const { values, setFieldValue } = useCardFormContext();
  const reminders = values[block.name];
  const dateFieldValue = values[block.dateFieldName];

  const shouldReset = !!reminders.length && !dateFieldValue;
  useEffect(() => {
    if (shouldReset) {
      setFieldValue(block.name, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReset]);
}
